import Vue from 'vue';
import Vuex from 'vuex';
import auth from "./modules/auth";
import alerts from "./modules/alerts-module";
import post from "./modules/post";
import category from "./modules/category";
import group from "./modules/group";
import chat from "./modules/chat";
import companyStructure from "./modules/company-structure";
import createPersistedState from "vuex-persistedstate";
import notification from "./modules/notification";
import notificationSetting from "./modules/notification-setting";
Vue.use(Vuex);
/** iOS Safari/WebView ~5MB — vượt hạn → QuotaExceededError ("The quota has been exceeded."). */
const VUEX_STORAGE_KEY = "vuex";
const VUEX_MAX_CHARS = 1_800_000;
function isQuotaExceededError(err) {
    if (!err || typeof err !== "object") {
        return false;
    }
    const e = err;
    return (e.name === "QuotaExceededError" ||
        e.name === "NS_ERROR_DOM_QUOTA_REACHED" ||
        e.code === 22 ||
        e.code === 1014 ||
        /quota/i.test(String(e.message || "")));
}
function safeJsonParse(raw) {
    if (!raw) {
        return null;
    }
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : null;
    }
    catch {
        return null;
    }
}
/** Giữ tối thiểu để còn login được sau khi localStorage đầy. */
function buildMinimalVuexPayload(raw) {
    const parsed = safeJsonParse(raw) || {};
    const auth = (parsed.auth && typeof parsed.auth === "object" ? parsed.auth : {});
    return JSON.stringify({
        auth: {
            isAuthenticated: !!auth.isAuthenticated,
            user: auth.user && typeof auth.user === "object" ? auth.user : {},
            userPosition: auth.userPosition || {},
            userTeam: auth.userTeam || {},
            userTitle: auth.userTitle || {},
            dictionaryTagSetting: auth.dictionaryTagSetting || {},
            chatLaunchNotice: null,
            modalIsOpen: false,
        },
    });
}
function shrinkVuexForStorage(raw) {
    if (raw.length <= VUEX_MAX_CHARS) {
        return raw;
    }
    const parsed = safeJsonParse(raw);
    if (!parsed) {
        return buildMinimalVuexPayload(null);
    }
    // Bỏ các khối nặng nhất trước — chat list / feed / company tree.
    delete parsed.post;
    delete parsed.alerts;
    delete parsed.category;
    delete parsed.companyStructure;
    delete parsed.notification;
    delete parsed.notificationSetting;
    if (parsed.chat && typeof parsed.chat === "object") {
        const chat = parsed.chat;
        delete chat.cacheChatMessages;
        delete chat.dataSocket;
        delete chat.dataReactionEmoji;
        delete chat.readReceiptSocket;
        // Chỉ giữ selected + unread; list lấy lại từ API.
        delete chat.chatGroups;
    }
    let next = JSON.stringify(parsed);
    if (next.length > VUEX_MAX_CHARS) {
        next = buildMinimalVuexPayload(raw);
    }
    return next;
}
const safeLocalStorage = {
    get length() {
        return localStorage.length;
    },
    clear() {
        localStorage.clear();
    },
    key(index) {
        return localStorage.key(index);
    },
    getItem(key) {
        return localStorage.getItem(key);
    },
    removeItem(key) {
        localStorage.removeItem(key);
    },
    setItem(key, value) {
        const payload = key === VUEX_STORAGE_KEY ? shrinkVuexForStorage(value) : value;
        try {
            localStorage.setItem(key, payload);
            return;
        }
        catch (err) {
            if (!isQuotaExceededError(err)) {
                throw err;
            }
        }
        // Quota đầy: dọn vuex cũ rồi ghi bản thu gọn / tối thiểu.
        try {
            localStorage.removeItem(VUEX_STORAGE_KEY);
            localStorage.setItem(key, shrinkVuexForStorage(payload));
            return;
        }
        catch {
            /* continue */
        }
        try {
            localStorage.removeItem(VUEX_STORAGE_KEY);
            localStorage.setItem(key, buildMinimalVuexPayload(payload));
        }
        catch {
            // Không chặn UI — mất persist còn hơn crash Messenger trên iOS.
        }
    },
};
export default new Vuex.Store({
    modules: {
        auth,
        alerts,
        post,
        category,
        group,
        chat,
        companyStructure,
        notification,
        notificationSetting
    },
    plugins: [
        createPersistedState({
            // Không persist `group` — danh sách nhóm (Nhóm làm việc, …) luôn lấy mới từ API.
            // Chat: không persist cacheChatMessages / socket / feed nặng (OOM + iOS QuotaExceeded).
            paths: [
                "auth",
                "chat.chatGroups",
                "chat.chatGroupSelected",
                "chat.unCountChatRead",
                "notificationSetting",
            ],
            storage: safeLocalStorage,
        }),
    ],
});
